/**
 * Search entry points: more ways into the search overlay.
 *
 * Each entry point is a `<button data-search-entry="<where>">` from
 * `snippets/search-entry.liquid`: the desktop header, the top of the mobile menu drawer, the
 * footer, the empty cart drawer and the empty /cart page. One listener on `document` serves
 * them all, so the cart drawer's field keeps working after the Section Rendering API has
 * replaced it. A click
 *
 *   1. records `search_entry_opened` with `{ entry }` in PostHog,
 *   2. closes the drawer the field sits in, if any, and waits until it has finished closing,
 *   3. opens the existing overlay: `showDialog()` on `#search-modal`, the call the header
 *      icon's `on:click="#search-modal/showDialog"` makes. No search logic lives here, and
 *      with Nosto's overlay switched on the same dialog hosts it.
 *
 * The header icon is Grebban's `snippets/search.liquid`. It opens the overlay itself, so its
 * clicks are only recorded, as `header_icon`, to measure the old spot beside the new ones.
 *
 * Why step 2 waits: both drawers animate out, and neither has finished when `close()`
 * returns. Horizon's `DialogComponent.closeDialog()` releases the scroll lock and restores the
 * scroll position only after its animation, so opening the overlay first would leave the page
 * scrollable underneath it and jump the page when it closes. `header-drawer` also keeps a
 * focus trap on `document` until its fade ends.
 *
 * Waiting used to show as two movements with a moment of bare page between them: the drawer
 * leaving, then the overlay arriving. Where the browser has view transitions, the hand-off
 * now runs inside one (`handOff`): the screen holds a still image of the page, the drawer
 * closes at once behind it, and the image fades out while the overlay slides in.
 *
 * Focus goes back to the menu button only after a keyboard press: that is where a keyboard
 * user expects to land. Whether a ring shows on it, or on anything else a dialog hands focus
 * back to, is not decided here but site-wide, by the last real input (input-modality.js).
 */

const EVENT_NAME = "search_entry_opened";

const ENTRY_SELECTOR = "[data-search-entry]";

/** The class header-actions passes to `snippets/search.liquid` for the header icon. */
const HEADER_ICON_SELECTOR = ".header-actions__search-icon";
const HEADER_ICON_ENTRY = "header_icon";

const OVERLAY_SELECTOR = "#search-modal";
const MENU_DRAWER_SELECTOR = "header-drawer";
const CART_DRAWER_SELECTOR = "cart-drawer-component";

/** Longest the overlay waits for a drawer to close. Both drawers animate out in about 200 ms. */
const DRAWER_CLOSE_TIMEOUT_MS = 600;

/**
 * What `#search-modal` fires once its dialog is open: dialog.js's `DialogOpenEvent`.
 * `showDialog()` calls `showModal()` a frame later, inside a requestAnimationFrame.
 */
const OVERLAY_OPEN_EVENT = "dialog:open";

/**
 * On <html> while a drawer hands over to the overlay; styled in snippets/search-entry.liquid.
 * HANDOFF_CLASS, from the start: names the page, which Horizon leaves unnamed, so the
 * transition has an image to hold. CLOSING_CLASS, once that image is taken: drawers close
 * without animating.
 */
const HANDOFF_CLASS = "search-entry-handoff";
const CLOSING_CLASS = "search-entry-handoff-closing";

/**
 * The entry point a click landed on, or null.
 *
 * @param {EventTarget | null} target
 * @returns {{entry: string, trigger: Element, opensOverlay: boolean} | null}
 */
export function entryFor(target) {
  if (typeof target?.closest !== "function") return null;

  const trigger = target.closest(ENTRY_SELECTOR);

  if (trigger) {
    return {
      entry: trigger.getAttribute("data-search-entry") || "unknown",
      trigger,
      opensOverlay: true,
    };
  }

  const icon = target.closest(HEADER_ICON_SELECTOR);

  if (icon) {
    return { entry: HEADER_ICON_ENTRY, trigger: icon, opensOverlay: false };
  }

  return null;
}

/**
 * Record an open in PostHog. Guarded like `trackSearchToPostHog` in nosto-search.js:
 * analytics must never stop the overlay from opening.
 *
 * @param {string} entry
 * @param {{capture?: Function}} [posthog]
 * @returns {boolean} Whether the event was handed to PostHog.
 */
export function trackEntry(entry, posthog = globalThis.posthog) {
  try {
    if (typeof posthog?.capture !== "function") return false;

    posthog.capture(EVENT_NAME, { entry });
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolves on the first `type` event from `target`, or after `timeoutMs`, whichever is first.
 *
 * @param {EventTarget} target
 * @param {string} type
 * @param {number} timeoutMs
 * @returns {Promise<unknown>}
 */
export function nextEvent(target, type, timeoutMs) {
  return Promise.race([
    new Promise((resolve) => {
      target.addEventListener(type, resolve, { once: true });
    }),
    new Promise((resolve) => {
      setTimeout(resolve, timeoutMs);
    }),
  ]);
}

/**
 * Whether `trigger` sits in a drawer that is open.
 *
 * @param {Element} trigger
 * @returns {boolean}
 */
export function inOpenDrawer(trigger) {
  const menu = trigger.closest(MENU_DRAWER_SELECTOR);
  const cart = trigger.closest(CART_DRAWER_SELECTOR);

  return Boolean(menu?.isOpen || cart?.refs?.dialog?.open);
}

/**
 * Run `update` inside a view transition, with the hand-off classes on <html> until it has
 * finished. Without view transitions, or when the shopper prefers reduced motion (Horizon's
 * own `startViewTransition` skips it then too), `update` simply runs.
 *
 * @param {() => Promise<void>} update
 * @param {Document} [doc]
 * @returns {Promise<void>} Settles when `update` has run.
 */
export function handOff(update, doc = globalThis.document) {
  const reduced = globalThis.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (reduced || typeof doc?.startViewTransition !== "function") {
    return update();
  }

  const { classList } = doc.documentElement;
  const done = () => classList.remove(HANDOFF_CLASS, CLOSING_CLASS);
  let transition;

  classList.add(HANDOFF_CLASS);

  // CLOSING_CLASS only once the still image is taken: the drawers hold their open state with
  // fill-mode animations, and switching those off any earlier would empty the image.
  try {
    transition = doc.startViewTransition(() => {
      classList.add(CLOSING_CLASS);
      return update();
    });
  } catch {
    done();
    return update();
  }

  // `finished` rejects only when `update` does; the caller hears of that below.
  transition.finished.finally(done).catch(done);

  return transition.updateCallbackDone;
}

/**
 * Close the drawer `trigger` sits in, and resolve once it has finished closing. Resolves at
 * once when `trigger` is not in an open drawer.
 *
 * @param {Element} trigger
 * @param {{timeoutMs: number, byKeyboard: boolean}} options
 */
async function closeDrawerAround(trigger, { timeoutMs, byKeyboard }) {
  const menu = trigger.closest(MENU_DRAWER_SELECTOR);

  if (menu?.isOpen) {
    const details = menu.refs.details;
    const closed = nextEvent(details, "toggle", timeoutMs);

    menu.close();
    await closed;

    // Nothing hands focus back when the menu closes, so the overlay would return it to
    // <body> on close. The menu button is where a keyboard user expects to land.
    if (byKeyboard) {
      details.querySelector("summary")?.focus({ preventScroll: true });
    }
    return;
  }

  const cart = trigger.closest(CART_DRAWER_SELECTOR);

  if (cart?.refs?.dialog?.open) {
    const closed = nextEvent(cart, "dialog:close", timeoutMs);

    cart.close();
    await closed;
  }
}

/**
 * True while a drawer hands over to the overlay: from the drawer starting to close until the
 * overlay's dialog is open, a frame after `showDialog()`. Until then the field can still take
 * a second tap. Outside a drawer the overlay opens at once and covers the field, so nothing is
 * held back.
 */
let handingOver = false;

/**
 * Open the overlay from an entry point. From a drawer, the drawer closes first and the two
 * happen inside `transition`. The overlay opens even if closing the drawer throws.
 *
 * @param {Element} trigger
 * @param {{showDialog: () => void}} overlay
 * @param {{timeoutMs?: number, byKeyboard?: boolean, transition?: typeof handOff}} [options]
 *   `byKeyboard`: the field was pressed from the keyboard, so focus returns to the menu
 *   button.
 */
export async function openFrom(
  trigger,
  overlay,
  {
    timeoutMs = DRAWER_CLOSE_TIMEOUT_MS,
    byKeyboard = false,
    transition = handOff,
  } = {},
) {
  if (!inOpenDrawer(trigger)) {
    overlay.showDialog();
    return;
  }

  // Held until the overlay reports it is open, or, if it never does, for twice `timeoutMs`:
  // waiting for the drawer is bounded by `timeoutMs`, so the overlay is open well before that
  // unless something hangs.
  handingOver = true;

  const timers = {};
  const release = () => {
    handingOver = false;
    clearTimeout(timers.fallback);
    overlay.removeEventListener?.(OVERLAY_OPEN_EVENT, release);
  };

  timers.fallback = setTimeout(release, 2 * timeoutMs);
  overlay.addEventListener?.(OVERLAY_OPEN_EVENT, release, { once: true });

  // No waiting for the overlay in here: the browser pauses animation frames while a view
  // transition updates, and showDialog() opens the dialog in one. That frame runs as soon as
  // this resolves, before the transition takes its second image, so the overlay is in it.
  await transition(async () => {
    try {
      await closeDrawerAround(trigger, { timeoutMs, byKeyboard });
    } finally {
      overlay.showDialog();
    }
  });
}

/**
 * The one click listener on `document`.
 *
 * @param {{target: EventTarget | null, detail: number}} event
 * @param {{doc?: Document, track?: typeof trackEntry, warn?: typeof console.warn}} [options]
 *   Injectable for the unit tests, which live in Jeanerica's integration repository rather
 *   than in this one. In the theme they are left to their defaults.
 */
export function onClick(
  event,
  {
    doc = globalThis.document,
    track = trackEntry,
    // eslint-disable-next-line no-console -- a failed hand-off is worth one line in the console.
    warn = (...args) => console.warn(...args),
  } = {},
) {
  const hit = entryFor(event.target);

  if (!hit) return;

  const overlay = doc.querySelector(OVERLAY_SELECTOR);

  // Missing, or dialog.js has not upgraded it yet: nothing can open, so nothing is recorded.
  if (typeof overlay?.showDialog !== "function") return;

  if (!hit.opensOverlay) {
    track(hit.entry);
    return;
  }

  // A second tap while a drawer is still closing must not open, or record, a second time.
  if (handingOver) return;

  track(hit.entry);
  // Enter or Space on a button fires a click with `detail` 0; a tap or a mouse click counts 1.
  // A failure closing a drawer is reported, not left unhandled. The overlay opens regardless:
  // openFrom calls showDialog() whether or not the drawer closed.
  openFrom(hit.trigger, overlay, { byKeyboard: event.detail === 0 }).catch(
    (error) =>
      warn("[search-entry] opening search failed from", hit.entry, error),
  );
}

if (typeof document !== "undefined") {
  document.addEventListener("click", onClick);
}
